import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import RichTextEditor from './RichTextEditor';
import { useT } from '../../i18n/LocaleContext';
import { formatDateInput } from '../../utils/format';
import { DOCUMENT_CATEGORIES } from './constants';

const emptyForm = {
  title: '',
  category: 'minutes',
  meetingDate: '',
  location: '',
  attendees: '',
  contentHtml: '',
};

export default function DocumentFormModal({ isOpen, onClose, onSubmit, initial }) {
  const t = useT();
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(initial ? {
      title: initial.title || '',
      category: initial.category || 'minutes',
      meetingDate: formatDateInput(initial.meetingDate),
      location: initial.location || '',
      attendees: initial.attendees || '',
      contentHtml: initial.contentHtml || '',
    } : { ...emptyForm, meetingDate: formatDateInput(new Date()) });
    setErrors({});
  }, [isOpen, initial]);

  const categoryOptions = useMemo(
    () => DOCUMENT_CATEGORIES.map((c) => ({ value: c, label: t(`documents_page.categories.${c}`) })),
    [t]
  );

  const set = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const next = {};
    if (!form.title.trim()) next.title = t('validation.required');
    if (!form.contentHtml) next.contentHtml = t('documents_page.content_required');
    setErrors(next);
    if (Object.keys(next).length) return;
    setSubmitting(true);
    try {
      await onSubmit({ ...form, title: form.title.trim() });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      closeOnBackdrop={false}
      title={isEdit ? t('documents_page.edit_document') : t('documents_page.new_document')}
      size="xl"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>{t('common.cancel')}</Button>
          <Button variant="gold" onClick={handleSubmit} loading={submitting}>
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </>
      )}
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-row form-row--doc">
          <FormField label={t('documents_page.field_title')} htmlFor="doc-title" required error={errors.title}>
            <Input id="doc-title" value={form.title} onChange={set('title')} invalid={Boolean(errors.title)} maxLength={200} autoFocus />
          </FormField>
          <FormField label={t('documents_page.field_category')} htmlFor="doc-category">
            <Select id="doc-category" options={categoryOptions} value={form.category} onChange={set('category')} />
          </FormField>
        </div>
        <div className="form-row form-row--2">
          <FormField label={t('documents_page.field_meeting_date')} htmlFor="doc-date" hint={t('common.optional')}>
            <Input id="doc-date" type="date" value={form.meetingDate} onChange={set('meetingDate')} />
          </FormField>
          <FormField label={t('documents_page.field_location')} htmlFor="doc-location" hint={t('common.optional')}>
            <Input id="doc-location" value={form.location} onChange={set('location')} maxLength={200} />
          </FormField>
        </div>
        <FormField label={t('documents_page.field_attendees')} htmlFor="doc-attendees" hint={t('documents_page.attendees_hint')}>
          <Textarea id="doc-attendees" value={form.attendees} onChange={set('attendees')} rows={2} maxLength={4000} />
        </FormField>
        <FormField label={t('documents_page.field_content')} htmlFor="doc-content" required error={errors.contentHtml}>
          <RichTextEditor
            id="doc-content"
            value={form.contentHtml}
            onChange={set('contentHtml')}
            placeholder={t('documents_page.content_placeholder')}
            invalid={Boolean(errors.contentHtml)}
          />
        </FormField>
      </form>
    </Modal>
  );
}
